/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import user from 'js/lib/user';

import { Grid, breakpoints } from '@coursera/cds-core';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import ContentGateEyebrow from 'bundles/video-logged-out-page/components/content-gates/ContentGateEyebrow';
import ContentGateFullPage from 'bundles/video-logged-out-page/components/content-gates/ContentGateFullPage';
import { LOGGED_OUT_VIDEO_ALLOWANCE, LS_USER_VIDEO_USAGE_ITEM } from 'bundles/video-logged-out-page/constants';
import type { LocalStorageVideoUsageItem, VideoPlayer } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

type Props = {
  showVideoContentGate: boolean;
  setShowVideoContentGate: (x: boolean) => void;
  setIsContentGatesActive?: (x: boolean) => void; // let parent know if content gates are active (in general, not just if video or full page gate is active)
  videoPlayer: VideoPlayer | null;
};

type State = {
  userVideosWatched: number;
  overVideoAllowance: boolean;
  showFullPageOverlay: boolean;
  clickedPlay: boolean;
  componentDidMount: boolean;
};

const styles = {
  contentGateStyles: css`
    .vlp-gate-eyebrow {
      min-height: 51px;
      padding: 8px 15px;
      background-color: var(--cds-color-blue-700);
      color: var(--cds-color-neutral-primary-invert);
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      ${breakpoints.down('xs')} {
        display: block;
      }

      .vlp-gate-eyebrow-text {
        margin: 0;
        ${breakpoints.down('xs')} {
          margin-bottom: 10px;
          text-align: center;
        }

        strong {
          ${breakpoints.down('xs')} {
            display: block;
          }
        }
      }

      .vlp-gate-eyebrow-button {
        margin-left: 50px;
        ${breakpoints.down('xs')} {
          margin-left: 0;
          min-width: 100%;
          min-height: 30px;
        }
      }
    }

    .vlp-gate-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      padding-top: 65px;
      background-color: var(--cds-color-blue-700);
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow-y: auto;
      ${breakpoints.down('xs')} {
        display: block;
      }

      .vlp-gate-overlay-close {
        position: absolute;
        top: 85px;
        right: 20px;
        background: transparent;
        border: none;
        height: 35px;
        width: 35px;
        padding: 0;

        ${breakpoints.down('md')} {
          display: none;
        }

        &::before {
          content: '';
          position: absolute;
          height: 100%;
          width: 2px;
          top: 0;
          right: 50%;
          background-color: var(--cds-color-neutral-background-primary);
          transform: rotate(-45deg);
        }

        &::after {
          content: '';
          position: absolute;
          height: 100%;
          width: 2px;
          top: 0;
          left: 50%;
          margin-left: -2px;
          background-color: var(--cds-color-neutral-background-primary);
          transform: rotate(45deg);
        }
      }

      .vlp-gate-overlay-content {
        max-width: 998px;
        padding: 25px;
        margin: auto;
        display: flex;
        ${breakpoints.down('md')} {
          display: block;
        }
      }

      .vlp-gate-overlay-column-one {
        width: 70%;
        padding-right: 60px;
        padding-bottom: 50px;
        border-right: 1px solid var(--cds-color-white-0);
        ${breakpoints.down('md')} {
          border-right: none;
          padding: 0;
          width: 100%;
        }
      }

      .vlp-gate-overlay-column-two {
        width: 30%;
        padding-top: 25px;
        padding-left: 40px;
        color: var(--cds-color-neutral-primary-invert);
        ${breakpoints.down('md')} {
          display: none;
        }

        h3 {
          margin-bottom: 25px;
        }

        .vlp-gate-overlay-list {
          li {
            line-height: 25px;
            margin-bottom: 15px;

            &:last-of-type {
              margin-bottom: 0;
            }
          }
        }
      }

      &--embedded-reg {
        .vlp-gate-overlay-content {
          align-items: center;
        }

        .vlp-gate-overlay-column-one {
          width: 60%;
          border: none;
          ${breakpoints.down('md')} {
            width: 100%;
          }
        }

        .vlp-gate-overlay-column-two {
          width: 40%;
          padding: 0;
          ${breakpoints.down('md')} {
            width: 100%;
            display: block;
          }

          .rc-AuthenticationModal-content {
            margin-top: 0;
          }
        }
      }
    }

    .vlp-gate-video-overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      display: flex;
      align-items: center;
      background: rgb(75 75 75 / 90%);
      z-index: 100;
    }

    .vlp-gate-video-overlay-content {
      padding: 25px 50px;
      ${breakpoints.down('xs')} {
        padding: 10px;
      }
    }
  `,
};

type VideoUsageItem = {
  videoPaths: string[];
  count: number;
};

export class ContentGateContainer extends React.Component<Props, State> {
  declare contentGatesEnabled: boolean;

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof ContentGateContainer.contextTypes>;

  state = {
    userVideosWatched: 0,
    overVideoAllowance: false,
    showFullPageOverlay: false,
    clickedPlay: false,
    componentDidMount: false,
  };

  constructor(props: Props) {
    super(props);

    this.contentGatesEnabled = !user.isAuthenticatedUser();
  }

  componentDidMount() {
    this.setState(
      () => ({ componentDidMount: true }),
      () => {
        this.checkIfOverVideoAllowance();
      }
    );
  }

  componentWillReceiveProps(nextProps: Props) {
    const { videoPlayer } = this.props;

    if (!videoPlayer && nextProps.videoPlayer) {
      nextProps.videoPlayer.on('play', this.incrementUserVideoUsageCount);
    }
  }

  componentDidUpdate() {
    this.shouldShowVideoGate();
  }

  getUserVideoUsageLocalStorage = () => {
    const { componentDidMount } = this.state;
    if (!componentDidMount || !localStorageEx.isAvailable()) return null;

    let lsVideoUsageJson: VideoUsageItem | null = localStorageEx.getItem<VideoUsageItem | null>(
      LS_USER_VIDEO_USAGE_ITEM,
      JSON.parse,
      null
    );
    if (lsVideoUsageJson && user.isAuthenticatedUser()) {
      // remove local storage item if user is authenticated and item is still present
      localStorageEx.removeItem(LS_USER_VIDEO_USAGE_ITEM);
    } else if (!lsVideoUsageJson) {
      lsVideoUsageJson = {
        videoPaths: [],
        count: 0,
      };
      localStorageEx.setItem(LS_USER_VIDEO_USAGE_ITEM, lsVideoUsageJson, JSON.stringify);
    }

    return lsVideoUsageJson;
  };

  checkIfOverVideoAllowance = () => {
    const { setIsContentGatesActive } = this.props;
    const { componentDidMount } = this.state;
    // if content gates are enabled, check if over video allowance
    if (componentDidMount && localStorageEx.isAvailable() && this.contentGatesEnabled) {
      const lsVideoUsageObj = this.getUserVideoUsageLocalStorage();

      if (lsVideoUsageObj && lsVideoUsageObj.count >= 0) {
        // don't show gate if it's one of the videos user has watched (and counted towards the video allowance)
        // overVideoAllowance is single source of truth, showFullPageOverlay is togglable
        const overVideoAllowance =
          lsVideoUsageObj.count >= LOGGED_OUT_VIDEO_ALLOWANCE && !this.hasUserWatchedVideo(lsVideoUsageObj);

        this.setState(() => ({
          overVideoAllowance,
          userVideosWatched: lsVideoUsageObj.count,
          showFullPageOverlay: overVideoAllowance,
        }));

        if (setIsContentGatesActive) {
          setIsContentGatesActive(overVideoAllowance);
        }
      }
    }
  };

  hasUserWatchedVideo = (videoUsageObj?: LocalStorageVideoUsageItem) => {
    // use this to see if the current video has already been counted towards the users video allowance
    const {
      router: {
        location: { pathname },
      },
    } = this.context;

    const lsVideoUsageObj = videoUsageObj || this.getUserVideoUsageLocalStorage();

    if (lsVideoUsageObj) {
      return _.includes(lsVideoUsageObj.videoPaths, pathname);
    } else {
      return false;
    }
  };

  incrementUserVideoUsageCount = () => {
    const {
      router: {
        location: { pathname },
      },
    } = this.context;

    if (this.contentGatesEnabled) {
      const lsVideoUsageObj = this.getUserVideoUsageLocalStorage();
      // only increment video count if it's a video they haven't seen
      if (
        lsVideoUsageObj &&
        lsVideoUsageObj.videoPaths &&
        lsVideoUsageObj.count < LOGGED_OUT_VIDEO_ALLOWANCE &&
        !this.hasUserWatchedVideo(lsVideoUsageObj)
      ) {
        localStorageEx.setItem(
          LS_USER_VIDEO_USAGE_ITEM,
          {
            videoPaths: [...lsVideoUsageObj.videoPaths, pathname],
            count: lsVideoUsageObj.count + 1,
          },
          JSON.stringify
        );
        this.setState(() => ({
          userVideosWatched: lsVideoUsageObj.count + 1,
          clickedPlay: true,
        }));
      }
    }
  };

  shouldShowVideoGate = () => {
    const { overVideoAllowance, showFullPageOverlay } = this.state;
    const { setShowVideoContentGate, showVideoContentGate } = this.props;

    if (!showVideoContentGate && overVideoAllowance && !showFullPageOverlay) {
      setShowVideoContentGate(true);
    }
  };

  toggleFullPageOverlay = () => {
    this.setState((state) => ({
      showFullPageOverlay: !state.showFullPageOverlay,
    }));
  };

  render() {
    const { userVideosWatched, showFullPageOverlay, clickedPlay } = this.state;
    // if logged in or exp is not active don't show gates
    if (!this.contentGatesEnabled) return null;

    // show full page gate if over video allowance
    if (showFullPageOverlay) {
      return (
        <Grid
          container
          css={styles.contentGateStyles}
          component={TrackedDiv}
          trackingName="vlp_content_gate_full_page"
          withVisibilityTracking={true}
          data={{
            isUserLoggedIn: user.isAuthenticatedUser(),
          }}
        >
          <ContentGateFullPage
            videoAllowance={LOGGED_OUT_VIDEO_ALLOWANCE}
            toggleOverlay={this.toggleFullPageOverlay}
            showOverlay={showFullPageOverlay}
          />
        </Grid>
      );
    } else {
      return (
        <TrackedDiv
          css={styles.contentGateStyles}
          trackingName="vlp_content_gate_eyebrow"
          withVisibilityTracking={true}
          data={{
            isUserLoggedIn: user.isAuthenticatedUser(),
          }}
        >
          <ContentGateEyebrow
            videosWatched={userVideosWatched}
            videoAllowance={LOGGED_OUT_VIDEO_ALLOWANCE}
            clickedPlay={clickedPlay}
          />
        </TrackedDiv>
      );
    }
  }
}

export default ContentGateContainer;
